import {useTranslation} from 'react-i18next'
import PropTypes from 'prop-types'
import {Text, Box, Link, Button, useBreakpointValue} from '@chakra-ui/react'
import {DownloadIcon} from '@chakra-ui/icons'

const pdfContainerStyles = {
  border: 'none',
  overflow: 'auto',
  '& object[type="application/pdf"]': {
    border: 'none !important',
  },
}

const Fallback = ({url, t}) => {
  return (
    <Box>
      <Box textAlign="center" p="6">
        <Text mb="4">{t('notSupported')}</Text>
      </Box>
      <ButtonOnly url={url} t={t} />
    </Box>
  )
}

Fallback.propTypes = {
  url: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
}

const HtmlObject = ({url, children}) => (
  <object
    key={url}
    allow="fullscreen"
    data={url}
    type="application/pdf"
    width="100%"
    height="100%"
    style={{border: 'none', display: 'block'}}
    data-testid="pdf-object"
  >
    {children}
  </object>
)

HtmlObject.propTypes = {
  url: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
}

const ButtonOnly = ({url, t}) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="center"
    height="100%"
    width="100%"
  >
    <Button
      href={url}
      variant="primary"
      as={Link}
      rightIcon={<DownloadIcon />}
      target="_blank"
    >
      {t('downloadPdf')}
    </Button>
  </Box>
)

ButtonOnly.propTypes = {
  url: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
}

const RenderPdf = ({
  url,
  toolbar,
  navpanes,
  pagemode,
  zoom,
  view,
  children,
  breakpoint,
}) => {
  const smallScreen = useBreakpointValue({
    base: true,
    [breakpoint]: false,
  })

  const {t} = useTranslation('renderPdf')

  const queryString = new URLSearchParams({
    toolbar,
    navpanes,
    pagemode,
    zoom,
    view,
  }).toString()

  const pdfUrl = `${url}#${queryString}`

  if (smallScreen) {
    return <ButtonOnly url={pdfUrl} t={t} />
  }

  return (
    <Box
      sx={pdfContainerStyles}
      height="100%"
      width="100%"
      data-testid="pdf-container"
    >
      <HtmlObject url={pdfUrl}>
        {children ?? <Fallback url={url} t={t} />}
      </HtmlObject>
    </Box>
  )
}

RenderPdf.defaultProps = {
  toolbar: 1,
  navpanes: 0,
  pagemode: 'none',
  zoom: 100,
  view: null,
  children: null,
  breakpoint: 'lg',
}

RenderPdf.propTypes = {
  url: PropTypes.string.isRequired,
  toolbar: PropTypes.number,
  navpanes: PropTypes.number,
  pagemode: PropTypes.string,
  zoom: PropTypes.number,
  view: PropTypes.string,
  children: PropTypes.node,
  breakpoint: PropTypes.string,
}

export default RenderPdf
